import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Languages, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { AIOT_PATH, errText, fetchCourse, fetchLecture, fetchMe, markDone } from "@/lib/aiot";
import { PortalSidebar } from "@/components/aiot/PortalSidebar";
import { AccessBanner, LockedClass, ClassMedia } from "@/components/aiot/PortalPanels";
import { TestsList, ResultCard } from "@/components/aiot/ProgressPanels";
import { LecturePlayer } from "@/components/aiot/LecturePlayer";
import { LabsOverview } from "@/components/aiot/LabsOverview";
import { EnrollNudge } from "@/components/aiot/EnrollNudge";

const NeedEnrol = () => {
  const { L } = useLang();
  return <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-600" data-testid="aiot-need-enrol">{L("Weekly tests and results open after your application is approved.", "साप्ताहिक टेस्ट और रिज़ल्ट आवेदन मंज़ूर होने के बाद खुलेंगे।")}</div>;
};

const LectureHead = ({ lec, wantHi }) => {
  const { L } = useLang();
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
        <span className={`rounded-full px-2.5 py-0.5 ${lec.kind === "demo" ? "bg-[#F0FDFA] text-[#0F766E]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>{lec.kind === "demo" ? L("Sample · Recorded", "सैंपल · रिकॉर्डेड") : L(`Class ${lec.class.n} · Month ${lec.class.month}`, `क्लास ${lec.class.n} · महीना ${lec.class.month}`)}</span>
        <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="w-3.5 h-3.5" />{lec.minutes} min</span>
        {lec.slides.length > 0 && <span className="inline-flex items-center gap-1 text-slate-500" data-testid="aiot-lecture-lang"><Languages className="w-3.5 h-3.5" />{lec.lang === "hi" ? "हिंदी नैरेशन" : wantHi ? "English नैरेशन (हिंदी जल्द)" : "English narration"}</span>}
      </div>
      <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0B1F3A]" data-testid="aiot-lecture-title">{lec.title}</h1>
      {lec.summary && <p className="mt-1 text-sm text-slate-600">{lec.summary}</p>}
    </div>
  );
};

export default function AiotPortal() {
  const { L, lang, setLang } = useLang();
  const [sp, setSp] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [me, setMe] = useState(null);
  const [lec, setLec] = useState(null);
  const [err, setErr] = useState("");
  const [nudge, setNudge] = useState(null);
  const sel = sp.get("view") || sp.get("lecture") || "d1";
  const ll = lang === "hi" ? "hi" : "en";
  const enrolled = course?.access?.enrolled;
  const isView = sel === "tests" || sel === "result" || sel === "labs";
  const cls = course?.classes.find((c) => c.id === sel);

  const loadMe = () => fetchMe().then(setMe).catch(() => {});
  useEffect(() => {
    document.title = "AIoT Diploma · Course portal | RoboSpark";
    fetchCourse().then((c) => { setCourse(c); if (c.access.enrolled) loadMe(); }).catch((e) => setErr(errText(e, "Could not load the course.")));
  }, []);
  useEffect(() => {
    if (!course || isView || (cls && !enrolled)) return;
    let live = true;
    setLec(null); setErr("");
    fetchLecture(sel, ll).then((d) => live && setLec(d)).catch((e) => live && setErr(errText(e, L("Could not load this lecture.", "यह लेक्चर लोड नहीं हुआ।"))));
    return () => { live = false; };
  }, [sel, ll, course]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (id) => { setSp(["tests", "result", "labs"].includes(id) ? { view: id } : { lecture: id }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const demoIdx = course?.demos.findIndex((d) => d.id === sel) ?? -1;
  const nextDemo = demoIdx >= 0 ? course.demos[demoIdx + 1] : null;
  const clsIdx = course?.classes.findIndex((c) => c.id === sel) ?? -1;
  const nextCls = clsIdx >= 0 ? course.classes[clsIdx + 1] : null;
  const nextBtn = nextDemo ? <button type="button" onClick={() => select(nextDemo.id)} className="rounded-full bg-[#0F766E] text-white px-5 h-11 inline-flex items-center gap-2 font-bold" data-testid="lecture-next-lecture">{L("Next sample", "अगला सैंपल")}<ArrowRight className="w-4 h-4" /></button>
    : clsIdx >= 0 ? (nextCls?.has_lecture ? <button type="button" onClick={() => select(nextCls.id)} className="rounded-full bg-[#0F766E] text-white px-5 h-11 inline-flex items-center gap-2 font-bold" data-testid="lecture-next-class">{L(`Next: Class ${nextCls.n}`, `अगली: क्लास ${nextCls.n}`)}<ArrowRight className="w-4 h-4" /></button> : null)
    : <Link to={`${AIOT_PATH}#apply`} className="rounded-full bg-[#0F766E] text-white px-5 h-11 inline-flex items-center gap-2 font-bold" data-testid="lecture-apply-cta">{L("Enroll for the full diploma", "पूरे डिप्लोमा के लिए एनरोल करें")}<ArrowRight className="w-4 h-4" /></Link>;

  const main = () => {
    if (sel === "labs") return <LabsOverview enrolled={enrolled} />;
    if (isView) return !enrolled ? <NeedEnrol /> : !me ? <Loader2 className="w-6 h-6 animate-spin text-[#0F766E]" /> : sel === "tests" ? <TestsList result={me.result} /> : <ResultCard result={me.result} />;
    if (cls && !enrolled) return <LockedClass cls={cls} access={course.access} />;
    if (err) return <div className="rounded-2xl bg-[#FEF2F2] border border-[#FECACA] p-4 text-sm font-semibold text-[#991B1B]" data-testid="aiot-portal-error">{err}</div>;
    if (!lec) return <div className="h-80 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#0F766E]" /></div>;
    return (
      <>
        <LectureHead lec={lec} wantHi={ll === "hi"} />
        {lec.slides.length ? <LecturePlayer lec={lec} next={nextBtn} onComplete={() => (enrolled ? markDone(lec.id).then(loadMe) : lec.kind === "demo" && setNudge(lec.id))} /> : <ClassMedia lec={lec} />}
        {!enrolled && nudge === lec.id && <EnrollNudge onClose={() => setNudge(null)} />}
        {lec.slides.length > 0 && lec.kind === "class" && <div className="mt-4"><ClassMedia lec={lec} /></div>}
      </>
    );
  };

  return (
    <div className="bg-[#F3F8FB] min-h-screen" data-testid="aiot-portal">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Link to={AIOT_PATH} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-[#0F766E]" data-testid="aiot-portal-back"><ArrowLeft className="w-4 h-4" />{L("Course page", "कोर्स पेज")}</Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm font-extrabold text-[#0B1F3A]">{L("Advanced AIoT Diploma · Course portal", "एडवांस्ड AIoT डिप्लोमा · कोर्स पोर्टल")}</span>
          <span className="flex-1" />
          <button type="button" onClick={() => setLang(lang === "hi" ? "en" : "hi")} className="rounded-full border border-slate-300 bg-white px-3 h-9 text-sm font-bold text-[#0B1F3A] inline-flex items-center gap-1.5 hover:border-[#0F766E]" data-testid="aiot-portal-lang">
            <Languages className="w-4 h-4" />{lang === "hi" ? "English" : "हिंदी"}
          </button>
        </div>
        {course && <AccessBanner access={course.access} />}
        {!course && !err && <div className="h-96 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#0F766E]" /></div>}
        {!course && err && <div className="mt-6 text-sm font-semibold text-[#991B1B]" data-testid="aiot-portal-error">{err}</div>}
        {course && (
          <div className="mt-5 grid lg:grid-cols-[320px_1fr] gap-6 items-start">
            <div className="order-2 lg:order-1"><PortalSidebar course={course} me={me} sel={sel} onSelect={select} /></div>
            <main className="order-1 lg:order-2 min-w-0">{main()}</main>
          </div>
        )}
      </div>
    </div>
  );
}
